import {
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
  useHover,
  useInteractions
} from '@floating-ui/react';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import clsx from 'clsx';
import ProgressIndicator from 'components/common/progress-indicator';
import XButton from 'components/inputs/x-button';
import { ConfigurationType, SelectedDuctedSystemType } from 'data/types';
import {
  setActiveDuctedSystem,
  setActiveSystemType,
  setSelectedDuctedSystems
} from 'features/systems';
import { produce } from 'immer';
import { MouseEvent, useEffect, useState } from 'react';
import { confirmationModal, warningModal } from 'utils/ui';
import DuctedSystemMap from '../systems/ducted-system-map';
import DuctlessSystemMap from '../systems/ductless-system-map';

type DuctedSystemOptionProps = {
  config: ConfigurationType;
  system: SelectedDuctedSystemType;
  isAccessoryPage?: boolean;
  hideDeletable?: boolean;
};

export default function DuctedSystemOptionComponent({
  config,
  system,
  isAccessoryPage,
  hideDeletable
}: DuctedSystemOptionProps) {
  const dispatch = useAppDispatch();
  const {
    selectedSystems,
    selectedDuctedSystems,
    activeDuctedSystem,
    activeSystemType
  } = useAppSelector((state) => state.systems);
  const [showPreview, setShowPreview] = useState(false);
  const [active, setActive] = useState(false);
  const [attentionNeeded, setAttentionNeeded] = useState(false);
  const [isHovering, setIsHovering] = useState(false);

  useEffect(() => {
    setActive(
      activeSystemType === 'ducted' &&
        selectedDuctedSystems.findIndex((sys) => sys.id === system.id) ===
          activeDuctedSystem
    );
    // Ducted systems don't have quotable system records yet, so default to false
    // This means ducted systems will always show as needing review
    setAttentionNeeded(true);
  }, [activeDuctedSystem, selectedDuctedSystems, activeSystemType, system.id]);

  const { refs, floatingStyles, context } = useFloating({
    open: showPreview,
    onOpenChange: setShowPreview,
    placement: 'bottom-start',
    strategy: 'fixed',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(10),
      shift({ padding: 5 }),
      flip({ fallbackAxisSideDirection: 'end' })
    ]
  });

  const hover = useHover(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([hover]);

  const handleClick = () => {
    dispatch(
      setActiveDuctedSystem(
        selectedDuctedSystems.findIndex((sys) => sys.id === system.id)
      )
    );
    dispatch(setActiveSystemType('ducted'));
  };

  const deleteSelf = () => {
    dispatch(
      setSelectedDuctedSystems(
        produce(selectedDuctedSystems, (draft) =>
          draft.filter((s) => s.id !== system.id)
        )
      )
    );
  };

  const handleDeleteClick = (
    event: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>
  ) => {
    event.stopPropagation();
    setShowPreview(false);
    if (
      isAccessoryPage ||
      system.selectedAccessories.some((acc) => acc.quantity > 0)
    ) {
      confirmationModal('Are you sure you want to delete this system?', () => {
        const systemIndex = selectedDuctedSystems.findIndex(
          (s) => s.id === system.id
        );
        if (activeDuctedSystem >= systemIndex) {
          dispatch(setActiveDuctedSystem(Math.max(0, activeDuctedSystem - 1)));
        }
        dispatch(
          setSelectedDuctedSystems(
            produce(selectedDuctedSystems, (draft) =>
              draft.filter((s) => s.id !== system.id)
            )
          )
        );
      });
    } else {
      deleteSelf();
    }
  };

  const classes = clsx(
    'inline-flex items-center rounded-full pl-2 shadow ring-1 ring-black ring-opacity-5 whitespace-nowrap relative',
    isAccessoryPage && active && 'bg-blue-100',
    isAccessoryPage && !active && 'bg-white hover:bg-gray-50',
    !isAccessoryPage && 'bg-white'
  );

  return (
    <div ref={refs.setReference} {...getReferenceProps()}>
      <div
        className={classes}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        {!hideDeletable && (!isAccessoryPage || isHovering) && (
          <XButton
            onClick={(e) => handleDeleteClick(e)}
            tooltip='Delete Selected System'
            onDisabledClick={() =>
              warningModal('You must have at least one selected system.')
            }
            disabled={
              isAccessoryPage &&
              selectedSystems.length + selectedDuctedSystems.length <= 1
            }
          />
        )}
        {isAccessoryPage && (!isHovering || hideDeletable) && (
          <ProgressIndicator size='sm' checked={!attentionNeeded} />
        )}
        <button
          type='button'
          className={`inline-flex items-center gap-2 py-2 pl-2 pr-3 ${
            isAccessoryPage ? 'cursor-pointer' : 'cursor-help'
          }`}
          onClick={isAccessoryPage ? handleClick : undefined}
        >
          <span className='text-sm font-semibold'>
            System{' '}
            {selectedDuctedSystems.findIndex((sys) => sys.id === system.id) + 1}
          </span>
          {!isAccessoryPage && (
            <span className='text-sm font-semibold text-green-600'>
              ${system.price}
            </span>
          )}
        </button>
      </div>
      {showPreview && (
        <div
          ref={refs.setFloating}
          className='z-20 max-w-lg'
          style={floatingStyles}
          {...getFloatingProps()}
        >
          <div className='flex flex-col gap-2 rounded-lg bg-white p-5 shadow-xl'>
            {config.condensers.map((condenser) =>
              condenser.type === 'ductless' ? (
                <DuctlessSystemMap
                  key={condenser.uniqueId}
                  condenser={condenser}
                  system={condenser.validSystems[0]}
                  showTags
                />
              ) : (
                <DuctedSystemMap
                  key={condenser.uniqueId}
                  condenser={condenser}
                  system={condenser.validDuctedSystems[0]}
                  showTags
                />
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
}
